import {
  Avatar,
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Pagination,
  Tag,
  Tooltip,
} from '@ads/components-react';
import {
  AcademicCapIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
  MapPinIcon,
  PencilSquareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  CourseRegistrationEnrollmentChildViewModel,
  CourseRegistrationEnrollmentParentViewModel,
} from '@/shared/api/generated';
import { Skeleton } from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

import { useEnrollmentGridChildren } from '../../../hooks/useEnrollmentGridChildren';
import { ENROLLMENT_CODE_TO_STATUS } from '../../../types/enrollmentStatusCode';
import { EnrollmentStatusBadge } from '../../atoms/EnrollmentStatusBadge';
import { EnrollmentSearchPrompt } from '../../molecules/EnrollmentSearchPrompt';

export interface EnrollmentTableProps {
  readonly parents: readonly CourseRegistrationEnrollmentParentViewModel[];
  readonly isLoading: boolean;
  readonly hasSearched: boolean;
  readonly totalParents: number;
  readonly totalCount: number;
  readonly totalPages: number;
  readonly pageSize: number;
  readonly pageIndex: number;
  readonly onPageChange: React.Dispatch<React.SetStateAction<number>>;
  readonly onManageEnrollment: (courseRegistrationId: number) => void;
  readonly onCancelEnrollment: (
    child: CourseRegistrationEnrollmentChildViewModel,
    parent: CourseRegistrationEnrollmentParentViewModel,
  ) => void;
  readonly onChangePolo: (
    child: CourseRegistrationEnrollmentChildViewModel,
    parent: CourseRegistrationEnrollmentParentViewModel,
  ) => void;
  readonly onEditPerson?: (courseRegistrationId: number) => void;
  readonly onBatchStatusChange: () => void;
}

const ROW_GRID = 'grid grid-cols-[2.5rem_minmax(0,1fr)_8.75rem_4.5rem_11.25rem_12.5rem]';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length <= 1) return first.slice(0, 2).toUpperCase();
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}

function TableHeader() {
  const { t } = useTranslation('personManagement');
  return (
    <div
      className={`${ROW_GRID} sticky top-none items-center border-b-hairline border-ctx-layout-border bg-ctx-layout-surface px-mini py-micro`}
    >
      <div />
      <span className="ts-subtitle-xxs text-ctx-content-title">
        {t('enrollmentList.table.courseStudent')}
      </span>
      <span className="ts-subtitle-xxs text-ctx-content-title">
        {t('enrollmentList.table.polo')}
      </span>
      <span className="ts-subtitle-xxs text-ctx-content-title">
        {t('enrollmentList.table.students')}
      </span>
      <span className="ts-subtitle-xxs text-ctx-content-title">{t('table.columns.status')}</span>
      <span className="ts-subtitle-xxs text-ctx-content-title">{t('table.columns.actions')}</span>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="flex flex-col divide-y divide-ctx-layout-border">
      {Array.from({ length: 5 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
        <div key={i} className={`${ROW_GRID} items-center px-xs py-xxs`}>
          <Skeleton className="h-xxs w-xxs rounded-sm" />
          <div className="flex flex-col gap-quark">
            <Skeleton className="h-xs w-[12rem] rounded-sm" />
            <Skeleton className="h-xs w-[8rem] rounded-sm" />
          </div>
          <Skeleton className="h-xs w-[5rem] rounded-sm" />
          <Skeleton className="h-xs w-[2rem] rounded-sm" />
          <Skeleton className="h-xs w-[6rem] rounded-sm" />
          <Skeleton className="h-xs w-[8rem] rounded-sm" />
        </div>
      ))}
    </div>
  );
}

function StudentActions({
  child,
  onManage,
  onCancel,
  onChangePolo,
  onEditPerson,
}: {
  child: CourseRegistrationEnrollmentChildViewModel;
  onManage: () => void;
  onCancel: () => void;
  onChangePolo: () => void;
  onEditPerson?: () => void;
}) {
  const { t } = useTranslation('personManagement');
  const statusKey = child.status ? ENROLLMENT_CODE_TO_STATUS[child.status] : undefined;
  const isDenied = statusKey
    ? ['rejected', 'canceled', 'canceled_in_registration', 'blocked'].includes(statusKey)
    : false;
  const isTransferred = statusKey
    ? ['transferred', 'transferred_in_registration'].includes(statusKey)
    : false;
  const canCancel = !isDenied && !isTransferred;

  return (
    <div className="flex items-center gap-xxs">
      <Button hierarchy="secondary" size="sm" onClick={onManage}>
        {t('enrollmentActions.manage')}
      </Button>

      <Dropdown
        trigger={
          <IconButton
            icon={EllipsisVerticalIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('enrollmentActions.moreOptions')}
          />
        }
        align="end"
      >
        <DropdownItem onSelect={onChangePolo}>
          <DropdownItemContent leadingIcon={MapPinIcon}>
            {t('enrollmentActions.changePolo')}
          </DropdownItemContent>
        </DropdownItem>

        {onEditPerson && (
          <DropdownItem onSelect={onEditPerson}>
            <DropdownItemContent leadingIcon={PencilSquareIcon}>
              {t('enrollmentActions.editPerson')}
            </DropdownItemContent>
          </DropdownItem>
        )}

        {canCancel && (
          <DropdownItem status="destructive" onSelect={onCancel}>
            <DropdownItemContent leadingIcon={XCircleIcon}>
              {t('enrollmentActions.cancel')}
            </DropdownItemContent>
          </DropdownItem>
        )}
      </Dropdown>
    </div>
  );
}

function StudentRow({
  child,
  onManage,
  onCancel,
  onChangePolo,
  onEditPerson,
}: {
  child: CourseRegistrationEnrollmentChildViewModel;
  onManage: () => void;
  onCancel: () => void;
  onChangePolo: () => void;
  onEditPerson?: () => void;
}) {
  const statusKey = child.status ? ENROLLMENT_CODE_TO_STATUS[child.status] : undefined;
  const initials = getInitials(child.studentName ?? '');

  return (
    <div
      className={`${ROW_GRID} h-[4.5rem] items-center rounded-md border-hairline border-ctx-layout-border px-xxs py-micro transition-colors hover:bg-ctx-interactive-secondaryHover`}
    >
      <div />
      <div className="flex min-w-none items-center gap-xs">
        <Avatar fallback={initials} size="md" />
        <div className="flex min-w-none flex-col">
          <span className="truncate ts-paragraph-xxs !font-medium text-ctx-content-title">
            {child.studentName ?? '—'}
          </span>
          {child.statusName && (
            <span className="truncate ts-caption-xxs text-ctx-content-placeholder">
              {child.statusName}
            </span>
          )}
        </div>
      </div>
      <div />
      <div />
      <div className="flex items-center">
        {statusKey && <EnrollmentStatusBadge status={statusKey} />}
      </div>
      <div className="flex items-center">
        <StudentActions
          child={child}
          onManage={onManage}
          onCancel={onCancel}
          onChangePolo={onChangePolo}
          {...(onEditPerson ? { onEditPerson } : {})}
        />
      </div>
    </div>
  );
}

function StudentRowsSection({
  parent,
  isOpen,
  onManage,
  onCancel,
  onChangePolo,
  onEditPerson,
}: {
  parent: CourseRegistrationEnrollmentParentViewModel;
  isOpen: boolean;
  onManage: (id: number) => void;
  onCancel: (
    child: CourseRegistrationEnrollmentChildViewModel,
    parent: CourseRegistrationEnrollmentParentViewModel,
  ) => void;
  onChangePolo: (
    child: CourseRegistrationEnrollmentChildViewModel,
    parent: CourseRegistrationEnrollmentParentViewModel,
  ) => void;
  onEditPerson?: (id: number) => void;
}) {
  const { t } = useTranslation('personManagement');
  const [childPage, setChildPage] = useState(0);

  const { children, isLoading, isFetching, isError, pageSize, totalCount, totalPages } =
    useEnrollmentGridChildren(parent.childrenFilter, isOpen, childPage + 1);

  const childPageCount = totalPages;

  if (!isOpen) return null;
  if (isLoading) {
    return (
      <div className="flex flex-col gap-nano bg-ctx-layout-body p-xxxs">
        <div
          className={`${ROW_GRID} h-[4.5rem] items-center rounded-md border-hairline border-ctx-layout-border px-xxs py-micro`}
        >
          <div />
          <Skeleton className="h-xs w-[12rem] rounded-sm" />
        </div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="bg-ctx-layout-body p-xxxs ts-caption-xxs text-ctx-interactive-destructive">
        {t('enrollmentList.childrenError')}
      </div>
    );
  }
  if (children.length === 0) {
    return (
      <div className="bg-ctx-layout-body p-xxxs ts-caption-xxs text-ctx-content-placeholder">
        {t('enrollmentList.childrenEmpty')}
      </div>
    );
  }
  return (
    <div
      className={`flex flex-col gap-nano bg-ctx-layout-body p-xxxs${
        isFetching && !isLoading ? ' opacity-medium' : ''
      }`}
    >
      {children.map(child => (
        <StudentRow
          key={child.courseRegistrationId}
          child={child}
          onManage={() => onManage(child.courseRegistrationId ?? 0)}
          onCancel={() => onCancel(child, parent)}
          onChangePolo={() => onChangePolo(child, parent)}
          {...(onEditPerson
            ? {
                onEditPerson: () => onEditPerson(child.personId ?? 0),
              }
            : {})}
        />
      ))}
      {childPageCount > 1 && (
        <div className="flex items-center justify-between px-mini pt-nano">
          <span className="ts-caption-xxs text-ctx-content-placeholder">
            {childPage * pageSize + 1}–{Math.min((childPage + 1) * pageSize, totalCount)}{' '}
            {t('enrollmentList.childrenOf')} {totalCount}
          </span>
          <Pagination
            pageAmount={childPageCount}
            activeIndex={childPage}
            setActiveIndex={setChildPage}
            ellipsisLabel="…"
          />
        </div>
      )}
    </div>
  );
}

function GroupRow({
  parent,
  isOpen,
  onToggle,
}: {
  parent: CourseRegistrationEnrollmentParentViewModel;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`${ROW_GRID} h-[4.5rem] w-full content-center items-center border-b-hairline border-ctx-layout-border px-mini text-left transition-colors ${
        isOpen
          ? 'bg-ctx-interactive-secondary hover:bg-ctx-interactive-secondaryHover'
          : 'bg-ctx-layout-surface hover:bg-ctx-layout-body'
      }`}
    >
      <div className="flex items-center justify-center">
        <IconButton
          ariaLabel=""
          hierarchy="secondary"
          size="sm"
          icon={ChevronRightIcon}
          className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
        />
      </div>

      <div className="flex min-w-none items-center gap-xs">
        <AcademicCapIcon className="h-xxs w-xxs shrink-0 text-ctx-content-brand" />
        <div className="flex min-w-none flex-col">
          <Tooltip title={parent.courseName ?? '—'}>
            <span className="truncate ts-paragraph-xxs !font-semibold text-ctx-content-title">
              {parent.courseName ?? '—'}
            </span>
          </Tooltip>
          {parent.poloName && (
            <span className="truncate ts-caption-xxs text-ctx-content-placeholder">
              Polo: {parent.poloName}
            </span>
          )}
        </div>
      </div>

      <span className="min-w-none truncate ts-paragraph-xxs text-ctx-content-base">
        {parent.poloName ?? '—'}
      </span>

      <span className="ts-paragraph-xxs !font-semibold text-ctx-content-title">
        {parent.studentsNumber ?? 0}
      </span>

      <div />

      <div className="flex items-center">
        <Tag
          label={`${parent.studentsNumber ?? 0} aluno(s)`}
          size="sm"
          status="default"
          custom={{ text: 'text-nowrap' }}
        />
      </div>
    </button>
  );
}

/**
 * Tabela de matrículas agrupadas por curso/turma/polo, vinda do endpoint
 * /api/v1/courseregistration/enrollment/grid. Cada grupo (parent) tem seus
 * alunos (children) carregados sob demanda ao expandir a linha.
 */
export function EnrollmentTable({
  parents,
  isLoading,
  hasSearched,
  totalCount,
  totalPages,
  pageSize,
  pageIndex,
  onPageChange,
  onManageEnrollment,
  onCancelEnrollment,
  onChangePolo,
  onEditPerson,
}: EnrollmentTableProps) {
  const { t } = useTranslation('personManagement');
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const rangeStart = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeEnd = Math.min((pageIndex + 1) * pageSize, totalCount);

  const toggle = (key: string) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs">
        <div className="overflow-x-auto">
          <div className="min-w-[53.75rem]">
            <TableHeader />
            <TableSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (parents.length === 0) {
    return (
      <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs">
        <div className="overflow-x-auto">
          <div className="min-w-[53.75rem]">
            <TableHeader />
          </div>
        </div>
        <div className="p-xl">
          {hasSearched ? <EmptyState title={t('table.empty')} /> : <EnrollmentSearchPrompt />}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs">
      <div className="overflow-x-auto">
        <div className="min-w-[53.75rem]">
          <TableHeader />
          {parents.map(parent => {
            const key = `${parent.courseId ?? 0}-${parent.classStudentId ?? 0}-${parent.poloId ?? 0}`;
            const isOpen = openIds.has(key);
            return (
              <div key={key}>
                <GroupRow parent={parent} isOpen={isOpen} onToggle={() => toggle(key)} />
                <StudentRowsSection
                  parent={parent}
                  isOpen={isOpen}
                  onManage={onManageEnrollment}
                  onCancel={onCancelEnrollment}
                  onChangePolo={onChangePolo}
                  {...(onEditPerson ? { onEditPerson } : {})}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between border-t-hairline border-ctx-layout-border px-mini py-micro">
        <span className="ts-caption-xxs text-ctx-content-placeholder">
          {rangeStart}–{rangeEnd} de <strong className="text-ctx-content-base">{totalCount}</strong>{' '}
          {t('enrollmentList.totalLabel')}
        </span>
        {totalPages > 1 && (
          <Pagination
            pageAmount={totalPages}
            activeIndex={pageIndex}
            setActiveIndex={onPageChange}
            ellipsisLabel="…"
          />
        )}
      </div>
    </div>
  );
}
